import React, { useEffect, useState } from 'react';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { MARGIN_BASIS_CLIENT_TOTAL, MARGIN_BASIS_COST } from 'Helpers/marginPercentage';

import Button from 'CommonComponents/Button';
import SelectFromField from 'CommonComponents/SelectFromField';
import TextFormField from 'CommonComponents/TextFormField';

const monthDays = [
    { name: $t('January'), days: 31, month: 1 },
    { name: $t('February'), days: 28, month: 2 },
    { name: $t('March'), days: 31, month: 3 },
    { name: $t('April'), days: 30, month: 4 },
    { name: $t('May'), days: 31, month: 5 },
    { name: $t('June'), days: 30, month: 6 },
    { name: $t('July'), days: 31, month: 7 },
    { name: $t('August'), days: 31, month: 8 },
    { name: $t('September'), days: 30, month: 9 },
    { name: $t('October'), days: 31, month: 10 },
    { name: $t('November'), days: 30, month: 11 },
    { name: $t('December'), days: 31, month: 12 },
];

const LOGIN_RETENTION_MONTHS = [12, 24, 36];

const CreateOrganisationScheme = Yup.object().shape({
    name: Yup.string().required('This value is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    firstName: Yup.string().required('This value is required'),
    lastName: Yup.string().required('This value is required'),
    subscription_plan_id: Yup.number().integer().min(1).positive().required('This value is required'),
    number_events: Yup.number().integer().positive().nullable(true),
    number_seats: Yup.number().integer().positive().nullable(true),
});

const UpdateOrganisationScheme = Yup.object().shape({
    name: Yup.string().required('This value is required'),
    mfaRequired: Yup.bool().required('This value is required'),
    subscription_plan_id: Yup.number().integer().min(1).positive().required('This value is required'),
    number_events: Yup.number().integer().positive().nullable(true),
    number_seats: Yup.number().integer().positive().nullable(true),
    margin_display_basis: Yup.string().oneOf([MARGIN_BASIS_CLIENT_TOTAL, MARGIN_BASIS_COST]),
    // '' is "Life of the organisation".
    loginRetentionMonths: Yup.string().oneOf(['', ...LOGIN_RETENTION_MONTHS.map(String)]),
});

export default function SaveOrganisationForm(props) {
    const datesArray = (number = 31) => {
        const arr = [];
        for (let i = 0; i < number; i++) {
            arr.push(i + 1);
        }

        return arr;
    };

    const getMonthFromProps = () => {
        if (!props.financialMonth) {
            return null;
        }

        const row = monthDays.find((item) => {
            return item.month == props.financialMonth;
        });

        return row ? row.name : null;
    };

    const getMaxDateFromMonth = (month) => {
        const row = monthDays.find((item) => {
            return item.name === month;
        });

        return row ? row.days : 31;
    };

    const [financialMonth, setFinancialMonth] = useState(getMonthFromProps() || $t('December'));
    const [financialDay, setFinancialDay] = useState(props.financialDay || 31);
    const [dates, setDates] = useState(datesArray(getMaxDateFromMonth(getMonthFromProps() || $t('December'))));
    const [disabledEventsField, setDisabledEventsField] = useState(true);
    const [disabledSeatsField, setDisabledSeatsField] = useState(true);
    const [numberEvents, setNumberEvents] = useState(props.maxEventsNumber || 0);
    const [numberSeats, setNumberSeats] = useState(props.maxSeats || 0);
    const [planId, setPlanId] = useState(props.subscription_plan_id || null);

    const changeYear = (e) => {
        const month = e.target.value;
        const monthSet = monthDays.find((row) => {
            return row.name === month;
        });
        const defaultDay = monthSet ? monthSet.days : 30;

        setFinancialMonth(month);
        setFinancialDay(defaultDay);

        const datArray = datesArray(defaultDay);
        setDates(datArray);
    };

    const changeFinancialDay = (e) => {
        setFinancialDay(e.target.value);
    };

    const planEvents = (planId) => {
        const plan = props.subscriptionPlans.find((item) => {
            return item.id == planId;
        });

        if (!plan) {
            return 0;
        }

        return plan.custom_event_number ? props.maxEventsNumber || plan.max_events_number : plan.max_events_number;
    };

    const planSeats = (planId) => {
        const plan = props.subscriptionPlans.find((item) => {
            return item.id == planId;
        });

        if (!plan) {
            return 0;
        }

        return plan.custom_seats ? props.maxSeats || plan.seats : plan.seats;
    };

    const initValues = () => {
        const plan = props.subscriptionPlans[0];

        if (props.action === 'create') {
            return {
                name: props.name || '',
                subscription_plan_id: plan.id,
                email: props.email || '',
                firstName: props.firstName || '',
                lastName: props.lastName || '',
            };
        }

        return {
            name: props.name || '',
            subscription_plan_id: props.subscription_plan_id || '',
            mfaRequired: props.mfaRequired || false,
            currency_id: props.currencyId || '',
            margin_display_basis: props.marginDisplayBasis || MARGIN_BASIS_CLIENT_TOTAL,
            loginRetentionMonths: props.loginRetentionMonths ? String(props.loginRetentionMonths) : '',
        };
    };

    const valuesListener = (plId) => {
        const id = parseInt(plId);
        const customEventsNumberPlanIds = props.editablePlansIds?.events || [];
        const customSeatsNumberPlanIds = props.editablePlansIds?.seats || [];

        setPlanId(id);
        if (!planId && (props.maxEventsNumber || props.maxSeats)) {
            setNumberEvents(props.maxEventsNumber || 0);
            setNumberSeats(props.maxSeats || 0);
            setPlanId(id);
        } else {
            setNumberEvents(planEvents(id) || 0);
            setNumberSeats(planSeats(id) || 0);
        }

        if (customEventsNumberPlanIds.includes(id)) {
            setDisabledEventsField(!props.canChangePlan);
        } else {
            setDisabledEventsField(true);
        }

        if (customSeatsNumberPlanIds.includes(id)) {
            setDisabledSeatsField(!props.canChangePlan);
        } else {
            setDisabledSeatsField(true);
        }
    };

    const changeEventsNumber = (e) => {
        setNumberEvents(e.target.value);
    };
    const changeSeatsNumber = (e) => {
        setNumberSeats(e.target.value);
    };

    return (
        <Formik
            initialValues={initValues()}
            validationSchema={props.action === 'create' ? CreateOrganisationScheme : UpdateOrganisationScheme}
            onSubmit={(values, actions) => {
                let data = { ...values };

                const month = monthDays.find((item) => {
                    return item.name === financialMonth;
                });

                data.financialMonth = month.month;
                data.financialDay = financialDay;
                data.number_events = disabledEventsField ? null : numberEvents;
                data.number_seats = disabledSeatsField ? null : numberSeats;

                // Only the settings page shows the retention select, so only it
                // may send one; anywhere else the stored value is left alone.
                if (props.forModal || !props.canEdit || props.action === 'create') {
                    delete data.loginRetentionMonths;
                } else {
                    data.loginRetentionMonths = data.loginRetentionMonths === '' ? null : Number(data.loginRetentionMonths);
                }

                axios
                    .post(props.saveOrganisationUrl, data)
                    .then((response) => {
                        if (response.data.url) {
                            window.location.href = response.data.url;
                        } else {
                            props.postSaveHandler();
                        }
                    })
                    .catch((e) => {
                        actions.setErrors(e.response.data.errors);
                    })
                    .finally(() => {
                        actions.setSubmitting(false);
                    });
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue, setFieldTouched, initialValues }) => (
                <>
                    {props.forModal ? (
                        <>
                            <FormObserver valuesListener={valuesListener} />
                            <div className="tw-grid tw-grid-cols-1">
                                <TextFormField
                                    label={$t('Organisation Name')}
                                    required={true}
                                    type="text"
                                    placeholder={$t('Organisation Name')}
                                    name="name"
                                    disabled={!props.canEdit || !props.canEditDetails}
                                    horizontalLayout={true}
                                ></TextFormField>
                            </div>
                            <div className="tw-grid tw-grid-cols-1">
                                <SelectFromField
                                    label={$t('Subscription Plan')}
                                    as="select"
                                    name="subscription_plan_id"
                                    disabled={!props.canChangePlan}
                                    horizontalLayout={true}
                                >
                                    {props.subscriptionPlans.map((plan) => {
                                        return (
                                            <option key={plan.id} value={plan.id}>
                                                {plan.name}
                                            </option>
                                        );
                                    })}
                                </SelectFromField>
                            </div>
                            <div className="tw-grid tw-grid-cols-1">
                                <div className={'tw-grid-cols-3 tw-grid tw-gap-4'}>
                                    <div className={'tw-col-span-1'}>
                                        <label htmlFor={'maxEventsNumber'} className="text-input__label">
                                            {$t('Number of Events')}
                                        </label>
                                    </div>
                                    <div className={'tw-col-span-2 tw-flex tw-flex-col tw-space-y-1.5'}>
                                        <input
                                            className={'tw-border-0 text-input'}
                                            type="number"
                                            disabled={disabledEventsField}
                                            value={numberEvents}
                                            onChange={changeEventsNumber}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-1">
                                <div className={'tw-grid-cols-3 tw-grid tw-gap-4'}>
                                    <div className={'tw-col-span-1'}>
                                        <label htmlFor={'seats'} className="text-input__label">
                                            {$t('Seats')}
                                        </label>
                                    </div>
                                    <div className={'tw-col-span-2 tw-flex tw-flex-col tw-space-y-1.5'}>
                                        <input
                                            className={'tw-border-0 text-input'}
                                            type="number"
                                            disabled={disabledSeatsField}
                                            value={numberSeats}
                                            onChange={changeSeatsNumber}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="tw-grid tw-grid-cols-12 tw-gap-0 md:tw-gap-4">
                                <div className="tw-col-span-12 md:tw-col-span-4 tw-mb-1.5 md:tw-mb-0">
                                    <label className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Financial Year End')} *</label>
                                </div>
                                <div className="tw-col-span-12 md:tw-col-span-4 tw-mb-1.5 md:tw-mb-0">
                                    <select className="tw-border-0 select-input" value={financialMonth} onChange={changeYear} disabled={!props.canEditDetails}>
                                        {monthDays.map((row) => {
                                            return (
                                                <option key={row.name} value={row.name}>
                                                    {row.name}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className="tw-col-span-12 md:tw-col-span-4 tw-mb-1.5 md:tw-mb-0">
                                    <select
                                        className="tw-border-0 select-input"
                                        value={financialDay}
                                        onChange={changeFinancialDay}
                                        disabled={!props.canEditDetails}
                                    >
                                        {dates.map((date) => {
                                            return (
                                                <option key={date} value={date}>
                                                    {date}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            </div>
                            {props.action !== 'create' && (
                                <div className="tw-grid tw-grid-cols-1">
                                    <SelectFromField
                                        label={$t('MFA required')}
                                        as="select"
                                        name="mfaRequired"
                                        disabled={!props.canSetMfa}
                                        horizontalLayout={true}
                                        tooltipinfo={$t(
                                            'Enabling Multi-Factor Authentication (MFA) adds an additional layer of protection by requiring a verification code in addition to your password. This significantly reduces the risk of unauthorised access. Do you require all users to have multi-factor authentication to access their account?  If so, users will be instructed to set up MFA when they first log into the app.',
                                        )}
                                    >
                                        <option value={0}>No</option>
                                        <option value={1}>Yes</option>
                                    </SelectFromField>
                                </div>
                            )}
                            {props.action === 'create' && (
                                <>
                                    <div className="tw-grid tw-grid-cols-1">
                                        <TextFormField
                                            label={$t('Admin Email')}
                                            required={true}
                                            type="email"
                                            placeholder="Admin Email"
                                            name="email"
                                            horizontalLayout={true}
                                        ></TextFormField>
                                    </div>
                                    <div className="tw-grid tw-grid-cols-1">
                                        <TextFormField
                                            label={$t('Admin First Name')}
                                            required={true}
                                            type="text"
                                            name="firstName"
                                            placeholder="Admin First Name"
                                            horizontalLayout={true}
                                        ></TextFormField>
                                    </div>
                                    <div className="tw-grid tw-grid-cols-1">
                                        <TextFormField
                                            label={$t('Admin Last Name')}
                                            required={true}
                                            type="text"
                                            placeholder="Admin Last Name"
                                            name="lastName"
                                            horizontalLayout={true}
                                        ></TextFormField>
                                    </div>

                                    <div className="modal__footer">
                                        {props.action === 'create' && <Button onClick={props.cancelHandler}>Cancel</Button>}

                                        <Button variant="primary" disabled={isSubmitting ? true : false} onClick={handleSubmit}>
                                            Submit
                                        </Button>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div>
                            <FormObserver valuesListener={valuesListener} />
                            <div className="table__card">
                                <div className="tw-grid tw-grid-cols-6 tw-gap-x-4 tw-gap-y-8">
                                    <div className="tw-col-span-6">
                                        <TextFormField
                                            label={$t('Organisation Name')}
                                            required={true}
                                            type="text"
                                            placeholder="Organisation name"
                                            name="name"
                                            disabled={!props.canEdit || !props.canEditDetails}
                                            horizontalLayout={true}
                                        ></TextFormField>
                                    </div>

                                    <div className="tw-col-span-2">
                                        <SelectFromField
                                            label={$t('Subscription Plan')}
                                            as="select"
                                            name="subscription_plan_id"
                                            disabled={!props.canChangePlan}
                                            horizontalLayout={true}
                                        >
                                            {props.subscriptionPlans.map((plan) => {
                                                return (
                                                    <option key={plan.id} value={plan.id}>
                                                        {plan.name}
                                                    </option>
                                                );
                                            })}
                                        </SelectFromField>
                                    </div>
                                    <div className="tw-col-span-2">
                                        <div className={'tw-grid-cols-3 tw-grid tw-gap-4'}>
                                            <div className={'tw-col-span-1'}>
                                                <label htmlFor={'maxEventsNumber'} className="text-input__label">
                                                    {$t('Number of Events')}
                                                </label>
                                            </div>
                                            <div className={'tw-col-span-2 tw-flex tw-flex-col tw-space-y-1.5'}>
                                                <input
                                                    className={'tw-border-0 text-input'}
                                                    type="number"
                                                    disabled={disabledEventsField}
                                                    value={numberEvents}
                                                    onChange={changeEventsNumber}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="tw-col-span-2">
                                        <div className="tw-grid tw-grid-cols-1">
                                            <div className={'tw-grid-cols-3 tw-grid tw-gap-4'}>
                                                <div className={'tw-col-span-1'}>
                                                    <label htmlFor={'seats'} className="text-input__label">
                                                        {$t('Seats')}
                                                    </label>
                                                </div>
                                                <div className={'tw-col-span-2 tw-flex tw-flex-col tw-space-y-1.5'}>
                                                    <input
                                                        className={'tw-border-0 text-input'}
                                                        type="number"
                                                        disabled={disabledSeatsField}
                                                        value={numberSeats}
                                                        onChange={changeSeatsNumber}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="tw-col-span-2">
                                        <label className="text-input__label" style={{ fontSize: '14px' }}>
                                            {$t('Financial Year End')} <span>*</span>
                                        </label>
                                    </div>
                                    <div className="tw-col-span-2">
                                        <select
                                            className="tw-border-0 select-input"
                                            value={financialMonth}
                                            onChange={changeYear}
                                            disabled={!props.canEditDetails}
                                        >
                                            {monthDays.map((row) => {
                                                return (
                                                    <option key={row.name} value={row.name}>
                                                        {row.name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="tw-col-span-2">
                                        <select
                                            className="tw-border-0 select-input"
                                            value={financialDay}
                                            onChange={changeFinancialDay}
                                            disabled={!props.canEditDetails}
                                        >
                                            {dates.map((date) => {
                                                return (
                                                    <option key={date} value={date}>
                                                        {date}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>

                                    {props.action !== 'create' && (
                                        <div className="tw-col-span-2">
                                            <SelectFromField
                                                label={$t('Currency')}
                                                as="select"
                                                name="currency_id"
                                                disabled={!props.canEditDetails}
                                                horizontalLayout={true}
                                            >
                                                <option value="">{$t('Not set')}</option>
                                                {(props.currencies || []).map((currency) => {
                                                    return (
                                                        <option key={currency.id} value={currency.id}>
                                                            {currency.code} - {currency.name}
                                                        </option>
                                                    );
                                                })}
                                            </SelectFromField>
                                        </div>
                                    )}

                                    {props.action !== 'create' && (
                                        <div className="tw-col-span-6">
                                            <SelectFromField
                                                label={$t('MFA required')}
                                                as="select"
                                                name="mfaRequired"
                                                disabled={!props.canSetMfa}
                                                horizontalLayout={true}
                                                tooltipinfo={$t(
                                                    'Enabling Multi-Factor Authentication (MFA) adds an additional layer of protection by requiring a verification code in addition to your password. This significantly reduces the risk of unauthorised access. Do you require all users to have multi-factor authentication to access their account?  If so, users will be instructed to set up MFA when they first log into the app.',
                                                )}
                                            >
                                                <option value={0}>No</option>
                                                <option value={1}>Yes</option>
                                            </SelectFromField>
                                        </div>
                                    )}

                                    {props.action !== 'create' && (
                                        <div className="tw-col-span-6">
                                            {/* No colon anywhere in the tooltip key — i18next reads one as a
                                                namespace separator and the string renders empty. */}
                                            <SelectFromField
                                                label={$t('Margin % basis')}
                                                as="select"
                                                name="margin_display_basis"
                                                disabled={!props.canEdit}
                                                horizontalLayout={true}
                                                tooltipinfo={$t(
                                                    'On projects that report a mark-up, this decides what Margin % is quoted against — the Total (mark-ups ÷ expenses + mark-ups) or the Expenses (mark-ups ÷ expenses). It applies to every project this organisation runs.',
                                                )}
                                            >
                                                {/* Labels name the tiles a user sees; the stored values stay
                                                    `client_total` / `cost`, which every consumer keys on. */}
                                                <option value={MARGIN_BASIS_CLIENT_TOTAL}>{$t('Total')}</option>
                                                <option value={MARGIN_BASIS_COST}>{$t('Expenses')}</option>
                                            </SelectFromField>
                                        </div>
                                    )}

                                    {props.action !== 'create' && props.canEdit && (
                                        <div className="tw-col-span-6">
                                            <SelectFromField
                                                label={$t('Login record retention')}
                                                as="select"
                                                name="loginRetentionMonths"
                                                horizontalLayout={true}
                                                tooltipinfo={$t('How long each user login is kept. Older login records are deleted every night.')}
                                            >
                                                <option value="">{$t('Life of the organisation')}</option>
                                                <option value="12">{$t('12 months')}</option>
                                                <option value="24">{$t('24 months')}</option>
                                                <option value="36">{$t('36 months')}</option>
                                            </SelectFromField>
                                        </div>
                                    )}

                                    {props.action === 'create' && (
                                        <div className="tw-col-span-6">
                                            <TextFormField
                                                label={$t('Admin Email')}
                                                required={true}
                                                type="email"
                                                placeholder="Admin Email"
                                                name="email"
                                                horizontalLayout={true}
                                            ></TextFormField>
                                        </div>
                                    )}
                                    {props.action === 'create' && (
                                        <div className={'tw-col-span-6'}>
                                            <TextFormField
                                                label={$t('Admin First Name')}
                                                required={true}
                                                type="text"
                                                name="firstName"
                                                placeholder="Admin First Name"
                                                horizontalLayout={true}
                                            ></TextFormField>
                                        </div>
                                    )}
                                    {props.action === 'create' && (
                                        <div className={'tw-col-span-6'}>
                                            <TextFormField
                                                label={$t('Admin Last Name')}
                                                required={true}
                                                type="text"
                                                placeholder="Admin Last Name"
                                                name="lastName"
                                                horizontalLayout={true}
                                            ></TextFormField>
                                        </div>
                                    )}
                                </div>

                                <div className="tw-flex tw-justify-end tw-my-4">
                                    {props.action === 'create' && <Button onClick={props.cancelHandler}>Cancel</Button>}

                                    <Button variant="primary" disabled={!!isSubmitting} onClick={handleSubmit}>
                                        {$t('Submit')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </Formik>
    );
}

const FormObserver = (props) => {
    const { values } = useFormikContext();

    useEffect(() => {
        props.valuesListener(values.subscription_plan_id);
    }, [values]);

    return null;
};
